import { Form, Row, Col } from 'react-bootstrap';
import { useEffect, useState } from 'react';
import { getEmployees } from '../../services/firebase/employeeService';
import DepartmentSelector from '../common/DepartmentSelector';

const JobDetailsForm = ({ data, onChange, errors, formData }) => {
  const [managers, setManagers] = useState([]);

  const [selectedDeptObj, setSelectedDeptObj] = useState(null);

  useEffect(() => {
    fetchManagers();
    if (data.department) {
      import('../../services/firebase/departmentService').then(service => {
        service.getDepartmentById(data.department).then(dept => {
          setSelectedDeptObj(dept);
        });
      });
    }
  }, []);

  const fetchManagers = async () => {
    try {
      const response = await getEmployees();
      if (response.success) {
        const employees = response.data || [];
        setManagers(Array.isArray(employees) ? employees : []);
      }
    } catch (error) {
      console.error('Error fetching managers:', error);
    }
  };

  const handleChange = (field, value) => {
    onChange('job', field, value);
  };

  const handleDepartmentSelect = (dept) => {
    setSelectedDeptObj(dept);
    // Reset sub-department if main department changes
    if (data.subDepartment) {
      handleChange('subDepartment', '');
    }
  };

  const handlePasswordChange = (value) => {
    // Update password at root level of formData
    if (formData.onChange) {
      formData.onChange('initialPassword', value);
    }
  };

  const handleRootChange = (field, value) => {
    // For root level fields like initialPassword
    const event = new CustomEvent('rootFieldChange', {
      detail: { field, value }
    });
    window.dispatchEvent(event);

    // Auto-calculate probation end date
    if (field === 'joiningDate' || field === 'probationPeriod') {
      const joiningDate = field === 'joiningDate' ? value : formData.job.joiningDate;
      const probationPeriod = field === 'probationPeriod' ? value : formData.job.probationPeriod;

      if (joiningDate && probationPeriod) {
        const endDate = new Date(joiningDate);
        endDate.setMonth(endDate.getMonth() + parseInt(probationPeriod));
        onChange('job', 'probationEndDate', endDate.toISOString().split('T')[0]);
      }
    }
  };

  return (
    <div>
      <h5 className="mb-3">Job Details</h5>

      <Row className="mb-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Employee Code</Form.Label>
            <Form.Control
              type="text"
              value={data.employeeCode}
              onChange={(e) => handleChange('employeeCode', e.target.value)}
              placeholder="Auto-generated if left empty"
            />
            <Form.Text className="text-muted">
              Leave empty to auto-generate
            </Form.Text>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Department <span className="text-danger">*</span></Form.Label>
            <DepartmentSelector
              value={data.department}
              onChange={(value) => handleChange('department', value)}
              onSelect={handleDepartmentSelect}
              activeOnly={true}
              required={true}
              className={errors.department ? 'is-invalid' : ''}
            />
            {errors.department && (
              <div className="invalid-feedback d-block">{errors.department}</div>
            )}
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Sub-Department</Form.Label>
            <Form.Select
               value={data.subDepartment || ''}
               onChange={(e) => handleChange('subDepartment', e.target.value)}
               disabled={!selectedDeptObj || !selectedDeptObj.subDepartments || selectedDeptObj.subDepartments.length === 0}
            >
              <option value="">Select Sub-Dept</option>
              {selectedDeptObj?.subDepartments?.map((name, index) => (
                <option key={index} value={name}>{name}</option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted smaller">
              {!selectedDeptObj ? 'Select department first' : (!selectedDeptObj.subDepartments?.length ? 'No sub-depts found' : '')}
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Designation <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              value={data.designation}
              onChange={(e) => handleChange('designation', e.target.value)}
              isInvalid={!!errors.designation}
            />
            <Form.Control.Feedback type="invalid">{errors.designation}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Reporting Manager</Form.Label>
            <Form.Select
              value={
                managers.find(m => m.id === data.reportingManager)?.id ||
                managers.find(m => (m.personal?.fullName && m.personal?.fullName === data.reportingManager) || (m.fullName && m.fullName === data.reportingManager))?.id ||
                data.reportingManager ||
                ''
              }
              onChange={(e) => {
                const selectedId = e.target.value;
                handleChange('reportingManager', selectedId);
                const selectedManager = managers.find(m => m.id === selectedId);
                const managerName = selectedManager
                  ? (selectedManager.personal?.fullName || `${selectedManager.personal?.firstName || ''} ${selectedManager.personal?.lastName || ''}`.trim() || selectedManager.fullName || '')
                  : '';
                handleChange('reportingManagerName', managerName);
              }}
            >
              <option value="">Select Manager</option>
              {managers.map(manager => {
                const managerName = manager.personal?.fullName || `${manager.personal?.firstName || ''} ${manager.personal?.lastName || ''}`.trim() || manager.fullName || 'Unknown';
                const designation = manager.job?.designation || manager.designation || '';
                return (
                  <option key={manager.id} value={manager.id}>
                    {managerName}{designation ? ` - ${designation}` : ''}
                  </option>
                );
              })}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Employment Type</Form.Label>
            <Form.Select
              value={data.employmentType}
              onChange={(e) => handleChange('employmentType', e.target.value)}
            >
              <option value="">Select Type</option>
              <option value="Full-time">Full-time</option>
              <option value="Part-time">Part-time</option>
              <option value="Contract">Contract</option>
              <option value="Intern">Intern</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Joining Date <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="date"
              value={data.joiningDate}
              onChange={(e) => handleChange('joiningDate', e.target.value)}
              isInvalid={!!errors.joiningDate}
            />
            <Form.Control.Feedback type="invalid">{errors.joiningDate}</Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Probation Period (Months)</Form.Label>
            <Form.Control
              type="number"
              value={data.probationPeriod}
              onChange={(e) => handleChange('probationPeriod', e.target.value)}
              min="0"
              max="12"
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Probation End Date</Form.Label>
            <Form.Control
              type="date"
              value={data.probationEndDate || ''}
              readOnly
              disabled
              className="bg-light"
            />
            <Form.Text className="text-muted">
              Auto-calculated based on joining date and probation period
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Work Location</Form.Label>
            <Form.Control
              type="text"
              value={data.workLocation}
              onChange={(e) => handleChange('workLocation', e.target.value)}
              placeholder="e.g., Mumbai Office, Remote"
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Role</Form.Label>
            <Form.Select
              value={data.role}
              onChange={(e) => handleChange('role', e.target.value)}
            >
              <option value="employee">Employee</option>
              <option value="manager">Manager</option>
              <option value="dept_head">Department Head</option>
              <option value="production_head">Production Head</option>
              <option value="director">Director</option>
              <option value="hr">HR</option>
              <option value="admin">Admin</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <h6 className="mt-4 mb-3 text-primary">Login Credentials</h6>
      <Row className="mb-3">
        <Col md={12}>
          <Form.Group>
            <Form.Label>
              Initial Password {!formData.isEditMode && <span className="text-danger">*</span>}
            </Form.Label>
            <Form.Control
              type="text"
              value={formData.initialPassword || (formData.isEditMode ? '' : 'Welcome@123')}
              onChange={(e) => {
                // Update at root level
                if (window.updateInitialPassword) {
                  window.updateInitialPassword(e.target.value);
                }
              }}
              placeholder={formData.isEditMode ? "Password cannot be changed here" : "Enter initial password for employee"}
              readOnly={formData.isEditMode}
              disabled={formData.isEditMode}
              className={formData.isEditMode ? 'bg-light' : ''}
            />
            <Form.Text className="text-muted">
              {formData.isEditMode ? (
                <>
                  This shows the initial password set during employee creation.
                  <br />
                  <strong>Note:</strong> To reset password, use the password reset feature.
                </>
              ) : (
                <>
                  This password will be used for employee's first login. Employee can change it later.
                  <br />
                  <strong>Default:</strong> Welcome@123
                </>
              )}
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
};

export default JobDetailsForm;
